import React from 'react'
import { Typography, Tooltip } from '@mui/material'
import { snackbars } from '../../../infrastructure/snackbars'
import { DealContainer } from './dealEditStore'
import { Select, TextField, DateRangePicker } from '../../common/customComponents'
import { t } from '../../../infrastructure/i18nextHelper'
import { Button } from '../../common/customComponents'
import { assignableMovementsDialog } from './assignableMovements'

const tPrefix = 'deals.label.'

type StringDate = string

type ValidityPeriodDate = 'validFrom' | 'validTo'

export let ValidityPeriod = (): JSX.Element => {
    let store = DealContainer.useContainer()
    if (!store || !store.deal) return (<div />)
    let startfield: ValidityPeriodDate = 'validFrom' as ValidityPeriodDate
    let storedStartValue = store?.deal ? store.deal[startfield] : null
    let endfield: ValidityPeriodDate = 'validTo' as ValidityPeriodDate
    let storedEndValue = store?.deal ? store.deal[endfield] : null

    let setDates = (period) => {
        if (store.deal === null) return
        store.setDeal({ ...store.deal, validFrom: period.startDate, validTo: period.endDate })
    }

    return (
        <DateRangePicker
            startDate={storedStartValue}
            endDate={storedEndValue}
            status={store.fieldStatus.getStatus(startfield)}
            startLabel={t(tPrefix + startfield)}
            endLabel={t(tPrefix + endfield)}
            disabled={store.locked()}
            autoReplace={true}
            onPeriodChange={period => {
                if (period) setDates(period)
            }} />)
}

export let Assignee = (): JSX.Element => {
    let store = DealContainer.useContainer()
    let deal = store.deal
    let changeAssignee = (username: string) => {
        if (deal === null) return
        let assignee = store.assigneeChoices.first(x => x.userName === username)
        store.setDeal({ ...deal, assignee: assignee, assigneeOverwritten: true })
    }

    return (
        <Select label={t(tPrefix + 'assignee')}
            value={deal?.assignee?.userName ?? ''}
            status={store.fieldStatus.getStatus('assignee')}
            choices={store.assigneeChoices.map(x => ({ value: x.userName, text: x.fullName }))}
            onChange={value => { if (value) changeAssignee(value) }}
            auto={deal?.assigneeOverwritten}
            disabled={store.isInternal()} />
    )
}

type DutyStatusProps = {
    isLocked: boolean,
    disabledForLinkedOrAutonomous: boolean
}

export let DutyStatus = ({ isLocked, disabledForLinkedOrAutonomous }: DutyStatusProps): JSX.Element => {
    let store = DealContainer.useContainer()
    let deal = store.deal

    let onChange: (value: string) => void =
        isLocked
            ? () => { }
            : val => { if (val && !!deal) store.setDeal({ ...deal, dutyStatus: val }) }

    let isDisabled = isLocked || disabledForLinkedOrAutonomous || store.isDuplicate()

    let selectDutyStatus: JSX.Element =
        <Select
            label={t(tPrefix + 'dutyStatus')}
            status={store.fieldStatus.getStatus('dutyStatus')}
            hideOnSingleChoice
            value={deal?.dutyStatus ?? ''}
            choices={store.dutyStatuses}
            onChange={onChange}
            disabled={isDisabled} />

    if (isLocked)
        return (<Tooltip title={<Typography variant='body1'>{t('deals.label.mandatoryCompany')}</Typography>} placement='top'>
            <span>
                {selectDutyStatus}
            </span>
        </Tooltip>)
    else
        return selectDutyStatus
}

export let Currency = () => {
    let store = DealContainer.useContainer()
    let deal = store.deal
    return (<TextField
        label={t(tPrefix + 'currency')}
        disabled={true}
        text={deal?.currency}
        status={store.fieldStatus.getStatus('currency')}
        onChange={event => { if (deal !== null) store.setDeal({ ...deal, currency: event.target.value }) }}
    />)
}

export let AssignMovementButton = ({ buttonClass }: { buttonClass: string }): JSX.Element => {
    let store = DealContainer.useContainer()
    let onClickAssignMovement = async () => {
        store.highlightErrors()
        if (store.fieldStatus.getFieldsWithStatus('alert').length === 0) {
            if (store.assignableMovements?.length! > 0)
                assignableMovementsDialog.open()
            else
                snackbars.info(t('deals.label.noAssignableMovementFound'))
        }
        else
            store.setDeal({ ...store.deal! })
    }
    return (
        <Button
            label={t(tPrefix + 'assignMovementButton')}
            onClick={onClickAssignMovement}
            className={buttonClass} />)
}

export let Mot = (): JSX.Element => {
    let store = DealContainer.useContainer()
    let deal = store.deal

    return (
        <Select label={t(tPrefix + 'mot')}
            disabled={deal?.generateMktSaleForecasts || store.isInternal() || store.isPaperTransaction() || store.locked()}
            value={!store.isPaperTransaction() ? deal?.meanOfTransportation : ''}
            choices={store.meanOfTransportations}
            status={store.fieldStatus.getStatus('meanOfTransportation')}
            onChange={val => { if (!!deal && val) store.setDeal({ ...deal, meanOfTransportation: val }) }} />
    )
}
